/*
 * File: FilterModel.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Patient Appointment filter model
 */
import { types, getSnapshot, applySnapshot } from "mobx-state-tree";
import LocalStoragePrefs from "services/users/LocalStoragePrefs";
import mstIsNumber from "utils/MSTIsNumber";
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  endOfYear,
  subYears,
  startOfYear,
  format
} from "date-fns";

const FilterModel = types
  .model("FilterModel", {
    statusId: types.optional(types.number, 1),
    dateRange: types.optional(types.string, "2"),
    start: types.maybeNull(types.Date),
    end: types.maybeNull(types.Date),
    deleted: types.optional(types.boolean, false),
    inactive: true
  })

  .actions(self => ({
    update(key, value) {
      if (key === "dateRange") {
        self.calculateDatesFromRangeOption(value);
      }
      self[key] = mstIsNumber(self, key, value);
    },

    calculateDatesFromRangeOption(rangeOption) {
      const currentDate = new Date();
      switch (rangeOption) {
        //Custom Date Range, start/end already set
        case "1":
          break;
        //All
        case "2": {
          self.start = null;
          self.end = null;
          break;
        }
        //Future
        case "3": {
          self.start = currentDate;
          self.end = null;
          break;
        }
        //This Month
        case "4": {
          self.start = startOfMonth(currentDate);
          self.end = endOfMonth(currentDate);
          break;
        }
        //Last Month
        case "5": {
          const lastMonth = subMonths(currentDate, 1);
          self.start = startOfMonth(lastMonth);
          self.end = endOfMonth(lastMonth);
          break;
        }
        //Last 6 Months
        case "6": {
          const sixMonthsAgo = subMonths(currentDate, 6);
          self.start = sixMonthsAgo;
          self.end = currentDate;
          break;
        }
        //This Year
        case "7": {
          self.start = startOfYear(currentDate);
          self.end = endOfYear(currentDate);
          break;
        }
        //Last Year
        case "8": {
          const lastYear = subYears(currentDate, 1);
          self.start = startOfYear(lastYear);
          self.end = endOfYear(lastYear);
          break;
        }
        default: {
          break;
        }
      }
    },

    saveInLocalStorage(userStore) {
      let storage = new LocalStoragePrefs("patApptFilters", userStore);
      let modelObject = getSnapshot(self);
      Object.keys(modelObject).forEach(key => {
        storage.set(key, modelObject[key]);
      });
    },

    getFromLocalStorage(userStore) {
      let storage = new LocalStoragePrefs("patApptFilters", userStore);
      let results = storage.get();
      if (results) applySnapshot(self, results);
    }
  }))

  .views(self => ({
    createParameters() {
      var queryParameters = [];
      if (self.start !== null)
        queryParameters.push("start=" + format(self.start, "yyyy-MM-dd"));
      if (self.end !== null)
        queryParameters.push("end=" + format(self.end, "yyyy-MM-dd"));
      self.createParameter("deleted", queryParameters);
      if (self.statusId !== 1)
        self.createParameter("statusId", queryParameters);
      self.createParameter("inactive", queryParameters);
      return "/?" + queryParameters.join("&");
    },

    createParameter(parameterName, queryParameters) {
      let value = self[parameterName];
      if (value !== undefined && value !== null) {
        queryParameters.push(parameterName + "=" + value);
      }
    }
  }));

export default FilterModel;
